import { css } from '@emotion/react';
import { observer } from 'mobx-react-lite';
import { ReactElement, ReactNode, useLayoutEffect, useMemo, useRef } from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { useIntl } from 'react-intl';
import { ChatState, values } from 'sdk';

import { useSDK } from '../../../app';
import { useUIState } from '../../../app/App';
import { commonMessages } from '../../../i18n/commonMessages';
import { styled } from '../../../styled';
import { useTheme } from '../../../theme/useTheme';
import { CustomRenderThumbVertical } from '../../customRenderThumbVertical';
import { useAgreement } from '../fields/checkbox/hooks/useAgreement';
import { PersonalAgreementCheckbox } from '../fields/checkbox/personalAgreementCheckbox';
import { Tooltip } from '../fields/errorLabel';
import { MessageFieldPrechat } from '../fields/messageField/prechat';
import { Form } from '../form';
import { useForm } from './hooks/useForm';
import { useFormErrors } from './hooks/useFormErrors';
import { SendButton } from './sendButton';

const Wrapper = styled((props: { children: ReactNode; className: string }): ReactElement => <div {...props} />)(
	css`
		padding: 16px 16px;
	`
);

const bottomContainerStyles = {
	alignItems: 'center',
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'space-between',
	position: 'relative',
} as const;

const relative = { position: 'relative', display: 'flex' } as const;

export const Component = observer(({ className }: { className: string }): ReactElement => {
	const formRef = useRef<HTMLFormElement>(null);
	const wrapper = useRef<Scrollbars>(null);

	const { scrollStore } = useUIState();
	const theme = useTheme();
	const rootStore = useSDK();
	const isOffline = rootStore.uiStateStore.chatState === ChatState.offlineChat;
	const {
		component: checkboxLabel,
		hook: [checkboxChecked, setCheckboxChecked],
	} = useAgreement(
		rootStore.welcomeDataStore.data?.showcase.privacyPolicyHtml ?? '',
		theme.prechat.checkboxes.personalAgreement.labelLinkColor ?? '',
		theme.prechat.checkboxes.personalAgreement.labelColor ?? ''
	);
	const formValues = useMemo(
		() => values(isOffline ? rootStore.formsStore.offlineFields : rootStore.formsStore.onlineFields),
		[rootStore.formsStore.onlineFields, rootStore.formsStore.offlineFields, isOffline]
	);
	const { onSend, setMessage, message } = useForm(rootStore, formRef, checkboxChecked);
	const { messageError, personalAgreementError } = useFormErrors(rootStore);
	const { formatMessage } = useIntl();

	useLayoutEffect(() => {
		const target = wrapper.current;
		if (target) {
			scrollStore.setPrechatScroll(target);
		}
	}, [scrollStore]);

	return (
		<div className={className || '' + ' opening-form'}>
			<Scrollbars ref={wrapper} renderThumbVertical={CustomRenderThumbVertical}>
				<Wrapper>
					<Form errors={rootStore.formsStore.openingFormErrors} formRef={formRef} values={formValues} />
					<MessageFieldPrechat
						error={messageError}
						onSendClick={onSend}
						value={message}
						showSendIcon={false}
						onChange={setMessage}
						disabled={
							rootStore.startNewConversationRequestStatus.isPending ||
							rootStore.newConversationHistoryRequestStatus.isPending
						}
					/>
					<div
						css={css`
							div& {
								${bottomContainerStyles}
							}
						`}
					>
						{checkboxLabel && (
							<div
								css={css`
									div& {
										${relative}
									}
								`}
							>
								{personalAgreementError && !checkboxChecked && (
									<Tooltip>{personalAgreementError}</Tooltip>
								)}
								<PersonalAgreementCheckbox
									isOffline={isOffline}
									label={checkboxLabel}
									onChange={setCheckboxChecked}
								/>
							</div>
						)}
						<SendButton
							isOffline={isOffline}
							disabled={
								rootStore.startNewConversationRequestStatus.isPending ||
								rootStore.newConversationHistoryRequestStatus.isPending
							}
							onClick={onSend}
						>
							{formatMessage(commonMessages.send)}
						</SendButton>
					</div>
				</Wrapper>
			</Scrollbars>
		</div>
	);
});

export const OpeningForm = styled(Component)(
	css`
		height: 100%;
		overflow-y: auto;
	`
);
